/**
 * ÖĞRENCİ HAFTALIK PLANI — F-PPL-07b (04.10.2026, müşteri revizesi madde 5)
 *
 * Koç "Pazartesi 08:00–15:30 Okul, her hafta" diye düşünüyor; sunucu
 * başlangıç/bitiş anı + RFC 5545 tekrar kuralı saklıyor. Bu dosya iki
 * dil arasındaki çeviri — saf, React yok.
 *
 * SAAT YEREL: tarayıcı Türkiye saatinde. `new Date(y, a, g, s, d)` yerel
 * zamanı kuruyor ve ISO'ya çevirirken UTC'ye iniyor; sunucu UTC saklıyor
 * (CLAUDE.md kural 8). Tersi yönde de yerel getter'lar kullanılıyor.
 */

import { etkinliginOluslari } from "@koclukpanel/contracts";

export type Kategori = "SCHOOL" | "COURSE" | "PRIVATE_LESSON" | "SOCIAL" | "OTHER";

export const KATEGORI_ADI: Record<Kategori, string> = {
  SCHOOL: "Okul",
  COURSE: "Dershane",
  PRIVATE_LESSON: "Özel ders",
  SOCIAL: "Sosyal aktivite",
  OTHER: "Diğer",
};

export const GUN_ADLARI = [
  "Pazartesi",
  "Salı",
  "Çarşamba",
  "Perşembe",
  "Cuma",
  "Cumartesi",
  "Pazar",
] as const;

export interface PlanFormu {
  /** 1 = Pazartesi … 7 = Pazar (tekrarlı kayıtta). */
  gun: number;
  /** `YYYY-AA-GG` (tekrarsız kayıtta). */
  tarih: string;
  baslangicSaati: string;
  bitisSaati: string;
  tekrarli: boolean;
}

export interface PlanTanimi {
  id: string;
  baslik: string;
  aciklama: string | null;
  kategori: Kategori | null;
  baslangic: string;
  bitis: string;
  tekrarKurali: string | null;
}

const SAAT = /^([01]\d|2[0-3]):([0-5]\d)$/;

function yerelAn(tarih: string, saat: string): Date {
  const [y, a, g] = tarih.split("-").map(Number) as [number, number, number];
  const [s, d] = saat.split(":").map(Number) as [number, number];
  return new Date(y, a - 1, g, s, d, 0, 0);
}

function yerelGun(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function iki(n: number): string {
  return String(n).padStart(2, "0");
}

/** Bu haftanın (yerel) Pazartesisi + `gun - 1`. */
export function haftaninGunu(haftaBasi: string, gun: number): string {
  const d = yerelAn(haftaBasi, "12:00");
  d.setDate(d.getDate() + (gun - 1));
  return yerelGun(d);
}

export function formHatalari(f: PlanFormu): Partial<Record<keyof PlanFormu, string>> {
  const h: Partial<Record<keyof PlanFormu, string>> = {};
  if (!SAAT.test(f.baslangicSaati)) h.baslangicSaati = "Saat SS:DD biçiminde olmalı.";
  if (!SAAT.test(f.bitisSaati)) h.bitisSaati = "Saat SS:DD biçiminde olmalı.";
  if (!h.baslangicSaati && !h.bitisSaati && f.bitisSaati <= f.baslangicSaati) {
    // Gece yarısını aşan kayıt (22:00–01:00) bilerek desteklenmiyor: rutinler
    // gün içinde ve iki güne bölünen kayıt haftalık tabloda yanlış sütuna düşerdi.
    h.bitisSaati = "Bitiş başlangıçtan sonra olmalı (aynı gün içinde).";
  }
  if (f.tekrarli && !(f.gun >= 1 && f.gun <= 7)) h.gun = "Gün seçin.";
  if (!f.tekrarli && !/^\d{4}-\d{2}-\d{2}$/.test(f.tarih)) h.tarih = "Tarih seçin.";
  return h;
}

/**
 * Form → sunucu gövdesi. Tekrarlı kayıt BU HAFTANIN o gününden başlıyor:
 * gün geçmiş olsa da kayıt bu haftanın görünümünde yer alsın ve
 * program ekranı bu haftayı doğru göstersin.
 */
export function formdanGovde(
  f: PlanFormu,
  haftaBasi: string,
): { baslangic: string; bitis: string; tekrarKurali: string | null } {
  const tarih = f.tekrarli ? haftaninGunu(haftaBasi, f.gun) : f.tarih;
  return {
    baslangic: yerelAn(tarih, f.baslangicSaati).toISOString(),
    bitis: yerelAn(tarih, f.bitisSaati).toISOString(),
    tekrarKurali: f.tekrarli ? "FREQ=WEEKLY" : null,
  };
}

/** Sunucu kaydı → form (düzenleme açılınca). */
export function tanimdanForm(
  t: Pick<PlanTanimi, "baslangic" | "bitis" | "tekrarKurali">,
): PlanFormu {
  const b = new Date(t.baslangic);
  const s = new Date(t.bitis);
  return {
    gun: ((b.getDay() + 6) % 7) + 1,
    tarih: yerelGun(b),
    baslangicSaati: `${iki(b.getHours())}:${iki(b.getMinutes())}`,
    bitisSaati: `${iki(s.getHours())}:${iki(s.getMinutes())}`,
    tekrarli: t.tekrarKurali !== null,
  };
}

/**
 * DÜZENLEMEDE YALNIZCA DEĞİŞEN ALANLAR — Codex tur 36 B3 (05.10.2026).
 *
 * Düzenleme yeni kayıt dönüştürücüsünü (`formdanGovde`) kullanıyordu:
 * yalnızca açıklaması değişen, eylülde başlamış okul kaydının başlangıcı
 * bu haftaya taşınıyor ve kuralı koşulsuz `FREQ=WEEKLY` oluyordu —
 * geçmiş oluşlar kayboluyor, BYDAY / INTERVAL / UNTIL / COUNT siliniyordu.
 *
 * Kural:
 *  · Saat, gün ve tekrar seçimi DEĞİŞMEDİYSE zaman alanları HİÇ gönderilmez.
 *  · Tekrarlı kalan kayıtta yalnız saat/gün değiştiyse özgün başlangıç
 *    TARİHİ korunur (gün değiştiyse aynı haftada kaydırılır) ve kuralın
 *    gün dışındaki parçaları aynen kalır. Tek günlük `BYDAY` yeni güne
 *    çevrilir; çok günlü `BYDAY` korunur (form tek gün düzenliyor, birini
 *    seçmek ötekileri sessizce silerdi).
 *  · Tekrarlı → tekrarsız: kural `null`, tarih formdan.
 *  · Tekrarsız → tekrarlı: yeni kayıt gibi (bu haftadan, `FREQ=WEEKLY`).
 */
export function duzenlemeFarki(
  t: PlanTanimi,
  f: PlanFormu,
  ek: { kategori: Kategori; aciklama: string },
  haftaBasi: string,
): Partial<{
  baslik: string;
  aciklama: string | null;
  kategori: Kategori;
  baslangic: string;
  bitis: string;
  tekrarKurali: string | null;
}> {
  const fark: ReturnType<typeof duzenlemeFarki> = {};
  const aciklama = ek.aciklama.trim() || null;
  const baslik = aciklama ?? KATEGORI_ADI[ek.kategori];
  if (ek.kategori !== t.kategori) fark.kategori = ek.kategori;
  if (aciklama !== (t.aciklama ?? null)) fark.aciklama = aciklama;
  if (baslik !== t.baslik) fark.baslik = baslik;

  const eski = tanimdanForm(t);
  const zamanAyni =
    f.tekrarli === eski.tekrarli &&
    f.baslangicSaati === eski.baslangicSaati &&
    f.bitisSaati === eski.bitisSaati &&
    (f.tekrarli ? f.gun === eski.gun : f.tarih === eski.tarih);
  if (zamanAyni) return fark;

  if (f.tekrarli && t.tekrarKurali !== null) {
    const ilk = new Date(t.baslangic);
    ilk.setHours(12, 0, 0, 0);
    ilk.setDate(ilk.getDate() + (f.gun - eski.gun));
    const tarih = yerelGun(ilk);
    fark.baslangic = yerelAn(tarih, f.baslangicSaati).toISOString();
    fark.bitis = yerelAn(tarih, f.bitisSaati).toISOString();
    if (f.gun !== eski.gun) {
      const kural = gunuDegistir(t.tekrarKurali, eski.gun, f.gun);
      if (kural !== t.tekrarKurali) fark.tekrarKurali = kural;
    }
    return fark;
  }

  const yeni = formdanGovde(f, haftaBasi);
  fark.baslangic = yeni.baslangic;
  fark.bitis = yeni.bitis;
  if (yeni.tekrarKurali !== t.tekrarKurali) fark.tekrarKurali = yeni.tekrarKurali;
  return fark;
}

const RRULE_GUN = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"] as const;

/** Tek günlük BYDAY'i yeni güne çevirir; diğer parçalara dokunmaz. */
function gunuDegistir(kural: string, eskiGun: number, yeniGun: number): string {
  return kural
    .split(";")
    .map((p) => {
      const [anahtar, deger] = p.split("=");
      if (anahtar?.trim().toUpperCase() !== "BYDAY" || deger === undefined) return p;
      const gunler = deger.split(",").map((g) => g.trim().toUpperCase());
      if (gunler.length !== 1 || gunler[0] !== RRULE_GUN[eskiGun - 1]) return p;
      return `${anahtar}=${RRULE_GUN[yeniGun - 1]}`;
    })
    .join(";");
}

/**
 * Haftalık tabloda hangi sütun(lar)da görünür.
 *
 * SUNUCUNUN HESABIYLA — Codex tur 36 B5. Önce burada ayrı bir yorumlayıcı
 * vardı ve yalnız BYDAY'e bakıyordu: INTERVAL=2 her hafta, geçmişte biten
 * UNTIL ve dolmuş COUNT yine görünüyordu. Şimdi program ekranını besleyen
 * aynı fonksiyon (`@koclukpanel/contracts` `etkinliginOluslari`) bu haftanın
 * penceresinde açılıyor; sütun, oluşun YEREL günü.
 */
export function haftadakiGunler(t: PlanTanimi, haftaBasi: string): number[] {
  const pencere = {
    baslangic: yerelAn(haftaBasi, "00:00"),
    bitis: new Date(yerelAn(haftaninGunu(haftaBasi, 7), "00:00").getTime() + 24 * 3600 * 1000),
  };
  const gunler = etkinliginOluslari(
    {
      baslangic: new Date(t.baslangic),
      bitis: new Date(t.bitis),
      tekrarKurali: t.tekrarKurali,
    },
    pencere,
  )
    .filter((o) => o.baslangic >= pencere.baslangic && o.baslangic < pencere.bitis)
    .map((o) => ((o.baslangic.getDay() + 6) % 7) + 1);
  return [...new Set(gunler)].sort((a, b) => a - b);
}
